import PropTypes from 'prop-types'
import { createContext, memo, useCallback, useEffect, useState } from 'react'
import {
  equalTo,
  isNullOrUndefined,
  isPresent,
  mapValues,
  noop,
  shallowArrayEquals,
} from 'utils'
import Head from 'components/table/Head'
import Column from 'components/table/Column'
import Body from 'components/table/Body'
import Row from 'components/table/Row'
import Loading from 'components/loading/Loading'
import TableError from 'components/table/TableError'
import Center from 'components/Center'
import usePrevious from 'utils/hooks/usePrevious'
import SortableColumn from 'components/table/SortableColumn'
import { useUpdatingRef } from 'utils/hooks/useUpdatingRef'

export const TableContext = createContext({
  columns: [],
  setColumns: noop,
  rows: {},
  addRow: noop,
  removeRow: noop,
  toggleRow: noop,
  setAllRows: noop,
  hasCheckbox: false,
  setHasCheckbox: noop,
  headerHeight: 0,
  setHeaderHeight: noop,
  error: false,
  loading: false,
  loadingMessage: '',
  emptyMessage: '',
  errorMessage: '',
  hoveredId: null,
  setHoveredId: noop,
  mergeHeadersOnNoContent: false,
})

const Table = memo(
  ({
    className,
    caption,
    error,
    loading,
    emptyMessage = '',
    loadingMessage = <Loading />,
    errorMessage = <TableError />,
    children,
    selectedIds,
    onSelectionChange = noop,
    onHoverChange = noop,
    hoveredId,
    lockSelection = false,
    style = {},
    mergeHeadersOnNoContent = false,
  }) => {
    const [columns, setColumns] = useState([])
    const [rows, setRows] = useState({})
    const [hasCheckbox, setHasCheckbox] = useState(false)
    const [headerHeight, setHeaderHeight] = useState(0)
    const [innerSelectedIds, setInnerSelectedIds] = useState([])
    const [innerHoveredId, setInnerHoveredId] = useState(null)

    const onSelectionChangeRef = useUpdatingRef(onSelectionChange)
    const onHoverChangeRef = useUpdatingRef(onHoverChange)

    const addRow = useCallback(
      ({ id, isDisabled, noCheckbox }) => {
        const rowIdContainer = {}
        setRows(oldRows => {
          rowIdContainer.rowId = isPresent(id)
            ? id
            : `rowId$$${Object.keys(oldRows).length + 1}`
          return {
            ...oldRows,
            [rowIdContainer.rowId]: {
              checked: false,
              isDisabled,
              noCheckbox,
            },
          }
        })
        return rowIdContainer
      },
      [setRows]
    )

    const toggleRow = useCallback(
      id => {
        setRows(oldRows =>
          !lockSelection && isPresent(oldRows[id])
            ? {
                ...oldRows,
                [id]: { checked: !oldRows[id].checked },
              }
            : oldRows
        )
      },
      [lockSelection]
    )

    const removeRow = useCallback(
      id => {
        setRows(oldRows => {
          const { [id]: omit, ...rest } = oldRows
          return rest
        })
      },
      [setRows]
    )

    const setAllRows = useCallback(
      checked => {
        if (lockSelection) return
        setRows(_rows =>
          mapValues(_row => ({
            ..._row,
            checked: _row.isDisabled || _row.noCheckbox ? false : checked,
          }))(_rows)
        )
      },
      [lockSelection]
    )

    useEffect(() => {
      if (isNullOrUndefined(selectedIds)) return

      setRows(oldRows =>
        Object.entries(oldRows).reduce(
          (result, [id, row]) => ({
            ...result,
            [id]: {
              ...row,
              checked: !!selectedIds.find(equalTo(id)),
            },
          }),
          {}
        )
      )
    }, [selectedIds])

    useEffect(() => {
      setInnerSelectedIds(
        Object.entries(rows)
          .filter(([, row]) => row.checked)
          .map(([id]) => id)
      )
    }, [rows])

    const prevInnerSelectedIds = usePrevious(innerSelectedIds)

    useEffect(() => {
      if (
        prevInnerSelectedIds &&
        !shallowArrayEquals(prevInnerSelectedIds, innerSelectedIds)
      ) {
        onSelectionChangeRef.current(innerSelectedIds)
      }
    }, [prevInnerSelectedIds, innerSelectedIds, onSelectionChangeRef])

    useEffect(() => {
      if (hoveredId === undefined || innerHoveredId === hoveredId) return
      onHoverChangeRef.current(hoveredId)
      setInnerHoveredId(hoveredId)
    }, [innerHoveredId, hoveredId, onHoverChangeRef])

    const setHoveredId = useCallback(
      newHoverId => {
        onHoverChangeRef.current(newHoverId)
        setInnerHoveredId(newHoverId)
      },
      [onHoverChangeRef]
    )

    return (
      <TableContext.Provider
        value={{
          columns,
          setColumns,
          rows,
          addRow,
          removeRow,
          toggleRow,
          setAllRows,
          hasCheckbox,
          setHasCheckbox,
          error,
          loading,
          loadingMessage,
          emptyMessage,
          errorMessage,
          headerHeight,
          setHeaderHeight,
          hoveredId: hoveredId === undefined ? innerHoveredId : hoveredId,
          setHoveredId,
          mergeHeadersOnNoContent,
        }}>
        <Center.Container
          tag="table"
          className={className}
          on={!error && loading}
          style={{ ...style, ...(loading && { pointerEvents: 'none' }) }}>
          {!error && loading && (
            <Center
              tag="caption"
              offsetY={headerHeight / 2}
              style={{ zIndex: 1 }}>
              <Loading />
            </Center>
          )}
          {caption && <caption className="hide_caption">{caption}</caption>}
          {children}
        </Center.Container>
      </TableContext.Provider>
    )
  }
)

Table.displayName = 'Table'
Table.Head = Head
Table.Head.displayName = 'Table.Head'

Table.Column = Column
Table.Column.displayName = 'Table.Column'

Table.SortableColumn = SortableColumn
Table.SortableColumn.displayName = 'Table.SortableColumn'

Table.Body = Body
Table.Body.displayName = 'Table.Body'

Table.Row = Row
Table.Row.displayName = 'Table.Row'

export default Table

Table.propTypes = {
  caption: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  emptyMessage: PropTypes.node,
  error: PropTypes.bool,
  errorMessage: PropTypes.node,
  lockSelection: PropTypes.bool,
  loading: PropTypes.bool,
  loadingMessage: PropTypes.node,
  onSelectionChange: PropTypes.func,
  selectedIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  onHoverChange: PropTypes.func,
  hoveredId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  style: PropTypes.object,
  mergeHeadersOnNoContent: PropTypes.bool,
}
